import React from "react";
import { MobileDrawer, MobileDrawerProps } from "../MobileDrawer/MobileDrawer";
import { Button, ButtonProps } from "@cmp-src/primitives/button";

type Action =
  | {
      label: React.ReactNode;
      onSelect?: () => void;
      destructive?: boolean;
      disabled?: boolean;
      isCancel?: boolean; // separate bottom card
      buttonProps?: ButtonProps;
    }
  | "separator";

export interface MobileActionSheetProps
  extends Omit<
    MobileDrawerProps,
    "children" | "below" | "ariaLabel" | "className"
  > {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title?: React.ReactNode;
  message?: React.ReactNode;
  actions: (Action | undefined)[];
  size?: "fit" | "full"; // propagate to drawer
}

export const MobileActionSheet: React.FC<MobileActionSheetProps> = ({
  isOpen,
  onOpenChange,
  portalContainer,
  dismissible,
  dragCloseThreshold,
  title,
  message,
  actions,
  size = "fit",
  revealGap
}) => {
  // Split cancel
  const cancelIndex = [...actions]
    .reverse()
    .findIndex((a) => a !== "separator" && a?.isCancel);
  const absoluteCancelIndex =
    cancelIndex === -1 ? -1 : actions.length - 1 - cancelIndex;

  const mainActions = actions.filter((_, i) => i !== absoluteCancelIndex);
  const cancelAction =
    absoluteCancelIndex >= 0
      ? (actions[absoluteCancelIndex] as Exclude<Action, "separator">)
      : null;

  return (
    <MobileDrawer
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      portalContainer={portalContainer}
      dismissible={dismissible}
      dragCloseThreshold={dragCloseThreshold}
      ariaLabel={String(title) || "Action Sheet"}
      size={size}
      revealGap={revealGap}
      below={
        cancelAction ? (
          <div className="p-3">
            <button
              className="w-full rounded-xl bg-neutral-100 px-4 py-3 text-center text-[15px] text-blue-600 transition hover:bg-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60 dark:bg-neutral-800 dark:text-blue-400 dark:hover:bg-neutral-700"
              onClick={() => onOpenChange(false)}
            >
              {cancelAction.label || "Cancel"}
            </button>
          </div>
        ) : null
      }
    >
      {(title || message) && (
        <div className="px-4 pb-3 text-center">
          {title && (
            <h2 className="font-semibold text-neutral-900 text-sm dark:text-neutral-100">
              {title}
            </h2>
          )}
          {message && (
            <p className="mt-1 text-neutral-600 text-xs dark:text-neutral-400">
              {message}
            </p>
          )}
        </div>
      )}

      <div className="px-3 pb-3">
        <div className="flex flex-col gap-2">
          {mainActions.map((action, idx) => {
            if (!action) return;

            if (action === "separator") {
              return (
                <div
                  key={`sep-${idx}`}
                  className="my-1 h-[1px] bg-neutral-200/80 dark:bg-neutral-700/60"
                  aria-hidden
                />
              );
            }
            return (
              <Button
                variant={"secondary"}
                {...action.buttonProps}
                onClick={() => {
                  action.onSelect?.();
                  onOpenChange(false);
                }}
                key={`${action.label}-${idx}`}
                disabled={!!action.disabled}
              >
                {action.label}
              </Button>
            );
          })}
        </div>
      </div>
    </MobileDrawer>
  );
};
